/**
 * La imagen de una pregunta (§9.3).
 *
 * **La descripción no es opcional en la práctica**: si falta, se avisa. No se bloquea guardar,
 * porque bloquear a un profesor a las ocho menos cuarto de la mañana hace que deje de poner
 * imágenes; pero el aviso está, y dice qué se pierde y quién lo pierde.
 *
 * El hueco se reserva con las dimensiones guardadas: sin ellas, el enunciado da un salto al llegar
 * la imagen, justo cuando la clase está leyendo.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signedImageUrl } from '../images/api.js';

export function ImagenPregunta({
  path,
  alt,
  width,
  height,
  className,
}: {
  path: string;
  alt: string | null;
  width: number | null;
  height: number | null;
  className?: string;
}): React.JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    void signedImageUrl(path).then((resultado) => {
      if (vigente) setUrl(resultado);
    });
    return () => {
      vigente = false;
    };
  }, [path]);

  /*
   * **Mientras la imagen no llega, se reserva su hueco.**
   *
   * Devolvía `null`, así que el enunciado se pintaba pegado a las respuestas y, al llegar la
   * imagen, todo saltaba hacia abajo: en el móvil, justo cuando ya se está leyendo. Con las
   * proporciones —que vienen del editor y viajan con la pregunta— el hueco existe desde el primer
   * fotograma y nada se mueve.
   *
   * Si no hay proporciones no se reserva nada: inventarse una sería mover la página igual pero
   * por otro sitio.
   */
  if (url === null) {
    if (width === null || height === null) return null;
    return (
      <div
        aria-hidden="true"
        className={`bg-surface border-line rounded-xl border-2 ${className ?? 'max-h-[45vh]'}`}
        style={{ aspectRatio: `${String(width)} / ${String(height)}` }}
      />
    );
  }

  return (
    <img
      src={url}
      // Vacío significa **decorativa** para un lector de pantalla, que es exactamente lo que hay que
      // decir cuando el profesor no ha escrito descripción: es preferible a leerle el nombre del
      // archivo.
      alt={alt ?? ''}
      {...(width === null ? {} : { width })}
      {...(height === null ? {} : { height })}
      loading="lazy"
      // Entra con opacidad y no con movimiento: sobre el hueco ya reservado, un fundido no
      // desplaza nada, y una imagen no lleva texto cuyo contraste pueda quedarse a medias.
      className={`entra-suave ${className ?? 'max-h-[45vh] w-auto rounded-xl object-contain'}`}
    />
  );
}

/** El campo de descripción, con su aviso. */
export function DescripcionImagen({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const vacia = value.trim() === '';

  return (
    <div className="mt-3 flex flex-col gap-1">
      <label htmlFor="imagen-alt" className="text-sm font-semibold">
        {t('images.altLabel')}
      </label>
      <input
        id="imagen-alt"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby="imagen-alt-ayuda"
        className="bg-surface border-line rounded-lg border px-3 py-2"
      />
      <p id="imagen-alt-ayuda" className="text-text-dim text-sm">
        {t('images.altHelp')}
      </p>
      {vacia ? (
        // Aviso, no bloqueo: bloquear a un profesor a las ocho menos cuarto hace que deje de poner
        // imágenes. Pero decir qué se pierde y quién lo pierde, sí.
        <p role="status" className="text-text-dim text-sm">
          <span aria-hidden="true">{WARN_MARK}</span>
          {t('images.altMissing')}
        </p>
      ) : null}
    </div>
  );
}

const WARN_MARK = '⚠ ';
